import {
  ExternalLink,
  AlertOctagon,
  Globe,
  Server,
  ChevronDown,
  ChevronUp,
  Shield,
  Gauge,
  Wrench,
  Target,
  FileText,
  Eye,
  EyeOff,
} from "lucide-react";
import { useState } from "react";
import type { ScoredFinding, ScoreFactors } from "@/lib/api";
import { RiskBadge } from "./RiskBadge";
import { ScorePill } from "./ScoreGauge";
import { EpssScore, EpssIndicator } from "./EpssScore";

interface FindingCardProps {
  finding: ScoredFinding;
  expanded?: boolean;
  className?: string;
}

const MODULE_ICONS: Record<string, React.ElementType> = {
  tech: Server,
  tls: Shield,
  headers: Globe,
  dns: Globe,
  email: Globe,
  cloud: Server,
  repos: Server,
  js: Server,
  portscan: Target,
};

function FactorBar({
  label,
  value,
  maxValue = 10,
  color = "cyan",
}: {
  label: string;
  value: number;
  maxValue?: number;
  color?: string;
}) {
  const percentage = (value / maxValue) * 100;
  const colors: Record<string, string> = {
    cyan: "bg-cyan-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    emerald: "bg-emerald-500",
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-medium">{value.toFixed(1)}</span>
      </div>
      <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${colors[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function ScoreFactorsDisplay({ factors }: { factors: ScoreFactors }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      <FactorBar
        label="Severity"
        value={factors.severity}
        color={factors.severity >= 7 ? "red" : factors.severity >= 4 ? "amber" : "cyan"}
      />
      <FactorBar
        label="Exploitability"
        value={factors.exploitability * 10}
        color={factors.exploitability >= 0.7 ? "red" : factors.exploitability >= 0.4 ? "amber" : "cyan"}
      />
      <FactorBar
        label="Asset Criticality"
        value={factors.asset_criticality}
        maxValue={3}
        color="amber"
      />
      <FactorBar
        label="Fix Difficulty"
        value={factors.remediation_difficulty}
        maxValue={3}
        color={factors.remediation_difficulty >= 2.5 ? "red" : "emerald"}
      />
    </div>
  );
}

// Helper to format evidence values for display
function formatEvidenceValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/50">null</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span className={value ? "text-emerald-400" : "text-red-400"}>
        {value.toString()}
      </span>
    );
  }
  if (typeof value === "number") {
    return <span className="text-cyan-400">{value}</span>;
  }
  if (typeof value === "string") {
    // Check if it's a URL
    if (value.match(/^https?:\/\//)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 hover:text-cyan-300 hover:underline inline-flex items-center gap-1"
        >
          {value}
          <ExternalLink className="h-3 w-3" />
        </a>
      );
    }
    // Check if it looks like a date
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return (
        <span className="text-amber-400">
          {new Date(value).toLocaleString()}
        </span>
      );
    }
    return <span className="text-foreground/90">{value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-muted-foreground/50">[]</span>;
    }
    return (
      <div className="space-y-1">
        {value.map((item, idx) => (
          <div key={idx} className="pl-2 border-l border-border/50">
            {formatEvidenceValue(item)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return <span className="text-muted-foreground/50">{"{}"}</span>;
    }
    return (
      <div className="space-y-1.5 pl-2 border-l border-border/50">
        {entries.map(([k, v]) => (
          <div key={k}>
            <span className="text-muted-foreground">{k}: </span>
            {formatEvidenceValue(v)}
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

// Evidence display component with collapsible toggle
function EvidenceDisplay({ context }: { context: Record<string, unknown> }) {
  const [showEvidence, setShowEvidence] = useState(false);

  const entries = Object.entries(context);
  if (entries.length === 0) return null;

  return (
    <div className="space-y-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowEvidence(!showEvidence);
        }}
        className="
          flex items-center gap-2 text-xs text-muted-foreground
          hover:text-cyan-400 transition-colors uppercase tracking-wider
          group
        "
      >
        {showEvidence ? (
          <EyeOff className="h-3 w-3 group-hover:text-cyan-400" />
        ) : (
          <Eye className="h-3 w-3 group-hover:text-cyan-400" />
        )}
        <FileText className="h-3 w-3" />
        <span>Evidence</span>
        <span className="px-1.5 py-0.5 rounded bg-muted/50 text-[10px] font-mono">
          {entries.length} {entries.length === 1 ? "field" : "fields"}
        </span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${showEvidence ? "rotate-180" : ""}`}
        />
      </button>

      {showEvidence && (
        <div
          className="
            rounded-lg bg-muted/20 border border-border/50 p-4
            font-mono text-xs space-y-3
            animate-in slide-in-from-top-2 duration-200
          "
        >
          {entries.map(([key, value]) => (
            <div key={key} className="space-y-1">
              <div className="text-cyan-500 font-semibold">{key}</div>
              <div className="pl-3">{formatEvidenceValue(value)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FindingCard({
  finding,
  expanded: initialExpanded = false,
  className = "",
}: FindingCardProps) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const Icon = MODULE_ICONS[finding.source_module] || Shield;

  const hasCves = finding.cve_ids.length > 0;
  const hasEpss = !!finding.epss;

  return (
    <div
      className={`
        rounded-lg border border-border bg-card/50
        hover:border-cyan-500/20 transition-all duration-200
        ${className}
      `}
    >
      {/* Header - always visible */}
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-4">
          {/* Rank badge */}
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
            <span className="text-sm font-mono font-bold text-muted-foreground">
              #{finding.rank}
            </span>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <RiskBadge level={finding.score.risk_level} size="sm" />
              {finding.is_kev && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[10px] font-semibold uppercase tracking-wider border border-red-500/30">
                  <AlertOctagon className="h-3 w-3" />
                  KEV
                </span>
              )}
              {hasEpss && <EpssIndicator epss={finding.epss!.epss} />}
            </div>

            <h4 className="font-medium text-foreground mb-1 pr-8">
              {finding.title}
            </h4>

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Icon className="h-3 w-3" />
                {finding.source_module}
              </span>
              {finding.asset_id && (
                <span className="font-mono truncate max-w-[200px]">
                  {finding.asset_id}
                </span>
              )}
              {hasCves && (
                <span className="text-amber-400">
                  {finding.cve_ids.length} CVE{finding.cve_ids.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          {/* Score and expand */}
          <div className="flex-shrink-0 flex items-center gap-3">
            <ScorePill score={finding.score.score} riskLevel={finding.score.risk_level} />
            <button className="p-1 rounded hover:bg-muted/50 transition-colors">
              {expanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-border/50 mt-0">
          <div className="pt-4 space-y-4">
            {/* Description */}
            {finding.description && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Gauge className="h-3 w-3" />
                  Description
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {finding.description}
                </p>
              </div>
            )}

            {/* CVE Links */}
            {hasCves && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <AlertOctagon className="h-3 w-3" />
                  Related CVEs
                </div>
                <div className="flex flex-wrap gap-2">
                  {finding.cve_ids.map((cve) => (
                    <a
                      key={cve}
                      href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="
                        inline-flex items-center gap-1 px-2 py-1 rounded
                        text-xs font-mono text-amber-400
                        bg-amber-500/10 border border-amber-500/30
                        hover:bg-amber-500/20 transition-colors
                      "
                    >
                      {cve}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* EPSS Details */}
            {hasEpss && (
              <EpssScore
                epss={finding.epss!}
                showCveLink={false}
                className="bg-muted/20"
              />
            )}

            {/* Score Factors */}
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Wrench className="h-3 w-3" />
                Score Factors
              </div>
              <ScoreFactorsDisplay factors={finding.score.factors} />

              {/* Known exploited boost */}
              {finding.score.factors.known_exploited && (
                <div className="mt-3 flex items-center gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                  <AlertOctagon className="h-4 w-4 text-red-400" />
                  <span className="text-xs text-red-400 font-medium">
                    1.5x score multiplier applied (CISA KEV listed)
                  </span>
                </div>
              )}
            </div>

            {/* Evidence - collapsible, hidden by default */}
            {finding.context && Object.keys(finding.context).length > 0 && (
              <EvidenceDisplay context={finding.context} />
            )}

            {/* Confidence */}
            <div className="flex items-center justify-between pt-2 border-t border-border/30">
              <span className="text-xs text-muted-foreground">
                Confidence Score
              </span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 bg-muted/30 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-cyan-500"
                    style={{ width: `${finding.score.confidence * 100}%` }}
                  />
                </div>
                <span className="text-xs font-mono font-medium">
                  {(finding.score.confidence * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact row variant for dense lists
export function FindingRow({
  finding,
  onClick,
}: {
  finding: ScoredFinding;
  onClick?: () => void;
}) {
  const Icon = MODULE_ICONS[finding.source_module] || Shield;

  return (
    <div
      className="
        flex items-center gap-4 px-4 py-3
        border-b border-border/30 last:border-0
        hover:bg-muted/30 cursor-pointer transition-colors
      "
      onClick={onClick}
    >
      <span className="w-8 text-center font-mono text-sm text-muted-foreground">
        {finding.rank}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{finding.title}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <Icon className="h-3 w-3" />
          <span>{finding.source_module}</span>
          {finding.asset_id && (
            <span className="font-mono truncate max-w-[150px]">
              {finding.asset_id}
            </span>
          )}
        </div>
      </div>
      <ScorePill score={finding.score.score} riskLevel={finding.score.risk_level} />
      <RiskBadge level={finding.score.risk_level} size="sm" />
      <span className="w-12 text-center text-xs text-muted-foreground">
        {finding.cve_ids.length > 0 ? (
          <span className="text-amber-400">{finding.cve_ids.length}</span>
        ) : (
          "—"
        )}
      </span>
    </div>
  );
}

export default FindingCard;
